import React, { useState, useEffect, useCallback } from 'react';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import CompetitionStatus from '../../common/CompetitionStatus/CompetitionStatus';
import ButtonSpinner from '../../common/ButtonSpinner/ButtonSpinner';
import DocxDownloadButton from '../../common/DocxDownloadButton/DocxDownloadButton';
import SubmissionReviewModal from '../../common/SubmissionReviewModal/SubmissionReviewModal';
import { useToast } from '../../../context/ToastContext';
import {
  getEnrollmentWorkspace, reviewSubmission, allowResubmission, adminUploadSubmission,
  updateSubmissionDeadline, getSubmissionFileUrl, decideResubmission,
} from '../../../services/competitionWorkflowService';
import useAuth from '../../../hooks/useAuth';
import { formatDate } from '../../../utils/helpers';
import styles from './ReviewPanel.module.css';

// Undoing an approval is a programme decision, not part of reviewing one piece
// of work — mentors are deliberately absent. Mirrors COMPETITION_ADMIN_ROLES
// on the backend, which is what actually enforces this.
const COMPETITION_ADMIN_ROLES = ['super_admin', 'manager', 'poc_owner'];

// Flatten the requirement tree into leaf components (skipping group containers).
const flattenLeaves = (nodes, parentTitle = null) => {
  const out = [];
  (nodes || []).forEach((n) => {
    if (n.submission_type === 'group') {
      out.push(...flattenLeaves(n.children, n.title));
    } else {
      out.push({ ...n, groupTitle: parentTitle });
    }
  });
  return out;
};

const LeafReview = ({ leaf, onChanged }) => {
  const toast = useToast();
  const { user } = useAuth();
  const isCompAdmin = COMPETITION_ADMIN_ROLES.includes(user?.role);
  const [adminLink, setAdminLink] = useState('');
  const sub = leaf.submission || {};
  // Opens the full-screen reader. A reviewer skimming three paragraphs in a
  // scroll box writes vague feedback; the anchored comments live in there.
  const [readerOpen, setReaderOpen] = useState(false);
  const [feedback, setFeedback] = useState(sub.feedback || '');
  const [newDeadline, setNewDeadline] = useState('');
  const [deadline, setDeadline] = useState(sub.internal_deadline || '');
  const [busyAction, setBusyAction] = useState(null); // 'approve' | 'request_changes' | 'deadline' | 'file'
  const busy = busyAction !== null;
  // An open ask from the student to redo this component.
  const resubRequest = leaf.resubmission_request?.status === 'pending'
    ? leaf.resubmission_request
    : null;
  const [resubNote, setResubNote] = useState('');

  const act = async (action) => {
    if (action === 'request_changes' && !feedback.trim()) {
      toast.warning('Please add feedback describing the changes needed.');
      return;
    }
    setBusyAction(action);
    try {
      await reviewSubmission(sub.id, {
        action,
        feedback: feedback.trim() || null,
        new_deadline: action === 'request_changes' && newDeadline ? newDeadline : null,
      });
      toast.success(action === 'approve' ? 'Approved.' : 'Changes requested.');
      onChanged();
    } catch (e) {
      toast.error(e.message || 'Review failed.');
    } finally {
      setBusyAction(null);
    }
  };

  const adminUpload = async (file, linkUrl) => {
    setBusyAction('adminUpload');
    try {
      await adminUploadSubmission(sub.id, { file, linkUrl });
      toast.success('Uploaded and marked approved.');
      setAdminLink('');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Upload failed.');
    } finally {
      setBusyAction(null);
    }
  };

  const reopen = async () => {
    setBusyAction('reopen');
    try {
      await allowResubmission(sub.id);
      toast.success('Reopened — the student can submit this again.');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not reopen.');
    } finally {
      setBusyAction(null);
    }
  };

  // The student asked to redo this one. Any reviewer who can see the student
  // may decide — including the mentor, who cannot reopen a component any other
  // way. Approving runs the same reopen as the button above.
  const decide = async (approve) => {
    setBusyAction(approve ? 'resubApprove' : 'resubDeny');
    try {
      await decideResubmission(resubRequest.id, approve, resubNote.trim() || null);
      toast.success(approve
        ? 'Reopened — the student can submit this again.'
        : 'Request denied. The student has been told.');
      setResubNote('');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not decide the request.');
    } finally {
      setBusyAction(null);
    }
  };

  const saveDeadline = async () => {
    setBusyAction('deadline');
    try {
      await updateSubmissionDeadline(sub.id, deadline || null);
      toast.success('Deadline updated.');
      onChanged();
    } catch (e) {
      toast.error(e.message || 'Could not update deadline.');
    } finally {
      setBusyAction(null);
    }
  };

  const openFile = async () => {
    setBusyAction('file');
    try {
      const url = await getSubmissionFileUrl(sub.id);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e.message || 'Could not open file.');
    } finally {
      setBusyAction(null);
    }
  };

  const status = sub.status || 'not_started';
  // Once a section is approved it's final — hide the review actions.
  const canReview = status === 'submitted' || status === 'changes_required';
  // Approved is the case this exists for: the student's workspace locks it and
  // the backend refuses to save it, so an early approval is otherwise stuck.
  const canReviewOrReopen = canReview || status === 'approved';

  return (
    <div className={styles.leaf}>
      <div className={styles.leafHead}>
        <div>
          <span className={styles.leafTitle}>
            {leaf.groupTitle ? `${leaf.groupTitle} · ` : ''}{leaf.title}
          </span>
          {leaf.locked && <span className={styles.lockTag}>🔒 Locked</span>}
        </div>
        <CompetitionStatus status={status} />
      </div>

      {/* Submitted content */}
      {sub.content && (
        <div className={styles.contentBox}>
          <div className={styles.wordCount}>
            {sub.word_count || 0} words
            <button type="button" className={styles.openReader} onClick={() => setReaderOpen(true)}>
              Open full screen &amp; comment
            </button>
          </div>
          <div className={styles.contentText}>{sub.content}</div>
        </div>
      )}
      <div className={styles.assets}>
        {sub.link_url && (
          <a href={sub.link_url} target="_blank" rel="noopener noreferrer" className={styles.assetLink}>🔗 Open link</a>
        )}
        {sub.file_path && (
          <button className={styles.assetLink} onClick={openFile} disabled={busy}>
            {busyAction === 'file' && <ButtonSpinner size={11} />}📎 {sub.file_name || 'Download file'}
          </button>
        )}
        {!sub.content && !sub.link_url && !sub.file_path && (
          <span className={styles.muted}>Nothing submitted yet.</span>
        )}
      </div>

      {/* Deadline editor */}
      <div className={styles.deadlineRow}>
        <label>Internal deadline</label>
        <input type="date" value={deadline || ''} onChange={(e) => setDeadline(e.target.value)} />
        <button className={styles.smallBtn} disabled={busy} onClick={saveDeadline}>
          {busyAction === 'deadline' && <ButtonSpinner size={11} />}Save
        </button>
        {sub.internal_deadline && <span className={styles.muted}>current: {formatDate(sub.internal_deadline)}</span>}
      </div>

      {isCompAdmin && (
        <div className={styles.deadlineRow}>
          <label>Admin upload</label>
          <input
            type="file"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) adminUpload(f, null);
              e.target.value = '';
            }}
          />
          <input
            type="url"
            placeholder="or paste a link…"
            value={adminLink}
            onChange={(e) => setAdminLink(e.target.value)}
            disabled={busy}
          />
          <button
            className={styles.smallBtn}
            disabled={busy || !adminLink.trim()}
            onClick={() => adminUpload(null, adminLink.trim())}
          >
            {busyAction === 'adminUpload' && <ButtonSpinner size={11} />}Save link
          </button>
          <span className={styles.muted}>Attaches to this component and marks it approved.</span>
        </div>
      )}

      {/* The student has asked to redo this. Sits above the reopen button and
          the review actions because it is the one thing here someone is
          actually waiting on. */}
      {resubRequest && (
        <div className={styles.resubAsk}>
          <div className={styles.resubAskHead}>🔄 Resubmission requested</div>
          <p className={styles.resubAskReason}>“{resubRequest.reason}”</p>
          <input
            className={styles.resubAskNote}
            value={resubNote}
            onChange={(e) => setResubNote(e.target.value)}
            placeholder="Optional note for the student…"
            disabled={busy}
          />
          <div className={styles.resubAskActions}>
            <button className={styles.changesBtn} disabled={busy} onClick={() => decide(false)}>
              {busyAction === 'resubDeny' && <ButtonSpinner size={12} />}Deny
            </button>
            <button className={styles.approveBtn} disabled={busy} onClick={() => decide(true)}>
              {busyAction === 'resubApprove' && <ButtonSpinner size={12} />}Allow resubmission
            </button>
          </div>
          <span className={styles.muted}>
            Allowing reopens the component so the student can edit and submit it again.
            They are messaged on WhatsApp either way.
          </span>
        </div>
      )}

      {canReviewOrReopen && isCompAdmin && (
        <div className={styles.deadlineRow}>
          <button className={styles.smallBtn} disabled={busy} onClick={reopen}>
            {busyAction === 'reopen' && <ButtonSpinner size={11} />}Allow resubmission
          </button>
          <span className={styles.muted}>
            {status === 'approved'
              ? 'Undoes the approval and lets the student submit again.'
              : 'Clears the review so the student can submit again.'}
          </span>
        </div>
      )}

      {/* Review actions */}
      {canReview && (
        <div className={styles.reviewArea}>
          <textarea
            className={styles.feedback}
            placeholder="Feedback for the student (required to request changes)…"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={2}
          />
          <div className={styles.reviewControls}>
            <input
              type="date"
              title="Revision deadline (optional)"
              value={newDeadline}
              onChange={(e) => setNewDeadline(e.target.value)}
            />
            <button className={styles.changesBtn} disabled={busy} onClick={() => act('request_changes')}>
              {busyAction === 'request_changes' && <ButtonSpinner size={12} />}Request Changes
            </button>
            <button className={styles.approveBtn} disabled={busy} onClick={() => act('approve')}>
              {busyAction === 'approve' && <ButtonSpinner size={12} />}Approve
            </button>
          </div>
        </div>
      )}
      {sub.revision_count > 0 && (
        <div className={styles.revisions}>Revisions requested: {sub.revision_count}</div>
      )}

      <SubmissionReviewModal
        open={readerOpen}
        submission={{ ...sub, name: leaf.title }}
        onClose={() => setReaderOpen(false)}
        onApprove={async () => {
          setReaderOpen(false);
          await act('approve');
        }}
        canReopen={isCompAdmin && canReviewOrReopen}
        onReopened={() => onChanged?.()}
        onEdited={() => {
          // The panel renders word count and status from its own copy of the
          // workspace, so a saved edit has to pull that through too.
          onChanged?.();
        }}
        onRequestChanges={() => {
          // The anchored comments are the detailed feedback; this just flips
          // the status, so it closes back to the form where the reviewer sets
          // a revision deadline rather than duplicating that here.
          setReaderOpen(false);
        }}
      />
    </div>
  );
};

const ReviewPanel = ({ enrollmentId, onChanged }) => {
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * `silent` refreshes the data without flipping `loading`.
   *
   * That flag is the whole fix for a real bug: the render guard below returns a
   * spinner whenever `loading` is true, which unmounts every LeafReview. So
   * approving one component used to tear down the entire modal and take with it
   * the feedback text, revision deadline and scroll position of every *other*
   * component the reviewer had been working through.
   *
   * A refresh after an action keeps the existing tree mounted and swaps the
   * data underneath it, so only the row that actually changed re-renders.
   */
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await getEnrollmentWorkspace(enrollmentId);
      setWorkspace(data);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [enrollmentId]);

  useEffect(() => { load(); }, [load]);

  const handleChanged = async () => {
    await load({ silent: true });
    // The parent reloads the cohort table behind the modal. It does not touch
    // `reviewEnrollment`, so this panel keeps the same enrollmentId and is not
    // remounted by it.
    if (onChanged) onChanged();
  };

  if (loading || !workspace) return <div className={styles.loading}><LoadingSpinner /></div>;

  const leaves = flattenLeaves(workspace.requirements);
  const { summary } = workspace;
  const statuses = summary.statuses || {};
  // Which components render as a .docx is decided per competition by the API.
  const documents = workspace.template?.documents || [];

  return (
    <div className={styles.panel}>
      <div className={styles.summaryBar}>
        <span>{summary.progress_done}/{summary.progress_total} approved</span>
        {summary.next_deadline && <span>Next deadline: {formatDate(summary.next_deadline)}</span>}
      </div>

      {documents.length > 0 && (
        <div className={styles.deliverables}>
          <span className={styles.deliverLabel}>Deliverables (.docx)</span>
          <div className={styles.deliverBtns}>
            {documents.map((d) => (
              <DocxDownloadButton
                key={d.doc_key}
                enrollmentId={enrollmentId} docKey={d.doc_key}
                label={d.title} approved={statuses[d.doc_key] === 'approved'}
              />
            ))}
          </div>
        </div>
      )}

      {leaves.map((leaf) => (
        <LeafReview key={leaf.id} leaf={leaf} onChanged={handleChanged} />
      ))}
    </div>
  );
};

export default ReviewPanel;
